/** Configurable radial, cone, directional, or coalesce particle burst primitive. */
import Phaser from 'phaser';
import { VfxPrimitive, VfxPrimitiveConfig } from './VfxPrimitive';
import { ensureCircleTexture } from '../../ui/CrispUtil';
import { TAU } from '../../utils/Math';

export interface ParticleBurstConfig extends VfxPrimitiveConfig {
  particleCount: number;
  minSpeed: number;
  maxSpeed: number;
  durationMs: number;
  spreadAngle: number;
  direction: number;
  mode: 'radial' | 'cone' | 'directional' | 'coalesce';
  easing: string;
  scaleStart?: number;
  scaleEnd?: number;
  alphaEnd?: number;
}

export class ParticleBurst extends VfxPrimitive<ParticleBurstConfig> {
  private textureKey!: string;
  private currentDirection: number = 0;

  /** Set the burst direction before calling play(). Config direction is added as an offset. */
  setDirection(angle: number): void {
    this.currentDirection = angle + (this.config.direction ?? 0);
  }

  protected allocatePool(): void {
    this.currentDirection = this.config.direction ?? 0;
    this.textureKey = ensureCircleTexture(this.scene, this.config.textureSize);
    for (let i = 0; i < this.config.particleCount; i++) {
      this.createPoolImage(this.textureKey);
    }
  }

  play(x: number, y: number, depth: number): void {
    this.killActiveTweens();
    const { config } = this;
    const scaleStart = config.scaleStart ?? 1;
    const scaleEnd = config.scaleEnd ?? 0.2;
    const alphaEnd = config.alphaEnd ?? 0;

    for (let i = 0; i < config.particleCount; i++) {
      const img = this.getFromPool(this.textureKey);
      const angle = this.computeAngle(i);
      const speed = config.minSpeed + Math.random() * (config.maxSpeed - config.minSpeed);
      const dist = speed * (config.durationMs / 1000);

      this.activate(img, x, y, depth);
      img.setScale(scaleStart);

      this.scene.tweens.add({
        targets: img,
        x: x + Math.cos(angle) * dist,
        y: y + Math.sin(angle) * dist,
        alpha: alphaEnd,
        scaleX: scaleEnd,
        scaleY: scaleEnd,
        duration: config.durationMs,
        ease: config.easing,
        onComplete: () => this.deactivate(img),
      });
    }
  }

  playCoalesce(x: number, y: number, depth: number): void {
    this.killActiveTweens();
    const { config } = this;
    const scaleStart = config.scaleStart ?? 1;
    const scaleEnd = config.scaleEnd ?? 0.2;

    for (let i = 0; i < config.particleCount; i++) {
      const img = this.getFromPool(this.textureKey);
      const angle = this.computeAngle(i);
      const speed = config.minSpeed + Math.random() * (config.maxSpeed - config.minSpeed);
      const dist = speed * (config.durationMs / 1000);

      const startX = x + Math.cos(angle) * dist;
      const startY = y + Math.sin(angle) * dist;

      this.activate(img, startX, startY, depth);
      img.setScale(scaleEnd).setAlpha(0);

      this.scene.tweens.add({
        targets: img,
        x,
        y,
        alpha: 1,
        scaleX: scaleStart,
        scaleY: scaleStart,
        duration: config.durationMs,
        ease: config.easing,
        onComplete: () => this.deactivate(img),
      });
    }
  }

  stop(): void {
    this.killActiveTweens();
    for (const img of this.pool) {
      if (img.active) {
        this.scene.tweens.add({
          targets: img,
          alpha: 0,
          duration: 100,
          onComplete: () => this.deactivate(img),
        });
      }
    }
  }

  private killActiveTweens(): void {
    for (const img of this.pool) {
      if (img.active) {
        this.scene.tweens.killTweensOf(img);
        this.deactivate(img);
      }
    }
  }

  private computeAngle(index: number): number {
    const { mode, spreadAngle, particleCount } = this.config;
    const dir = this.currentDirection;
    switch (mode) {
      case 'radial':
        return (TAU * index) / particleCount + (Math.random() - 0.5) * 0.4;
      case 'cone':
        return dir - spreadAngle / 2 + Math.random() * spreadAngle;
      case 'directional':
        return dir + (Math.random() - 0.5) * spreadAngle;
      case 'coalesce':
        return (TAU * index) / particleCount + (Math.random() - 0.5) * 0.4;
      default:
        return (TAU * index) / particleCount;
    }
  }
}
